export function formatPercentages(value: number): string {
  const percentageValue = value * 100;
  const finalPercentageValue: number = Math.round(percentageValue);
  const signedValue = finalPercentageValue > 0 ? `+${finalPercentageValue}` : finalPercentageValue;
  return `${signedValue}%`;
}

export function formatPercentageValue(value: number): number {
  const percentageValue = value * 100;

  // The percentage value should have no decimals
  return Math.round(percentageValue);
}

export function roundToDecimals(value: number, numDecimals: number): number {
  // Do NOT use value.toFixed(numDecimals). That's because we don't want fixed decimal size.
  // I.e., when numDecimals = 2, 21.7 will be 21.7, not 21.70.
  let powerOfTen = 1;
  for (; numDecimals; numDecimals--) {
    powerOfTen *= 10;
  }
  // Add Number.EPSILON to ensure things like 1.005 round correctly.
  return Math.round((value + Number.EPSILON) * powerOfTen) / powerOfTen;
}

/**
 * Should transform JSON from nested to flat
 * Example:
 * input: {"key1": "value1", "key2": {"key3":"value3", "key4": "value4" } }
 * output: {"key1": "value1", "key2.key3": "value3", "key2.key4": "value4" }
 *
 * @param nestedObject Object
 * @param excludedFields Array fields to be excluded
 * @param flatValuesEvenIfThereAreObjectArrays Boolean If true, flat values even if there are arrays of objects
 * @return Object
 */
export function flattenJson(
  nestedObject: object,
  excludedFields: string[] = [],
  flatValuesEvenIfThereAreObjectArrays: boolean = false
): object {
  const result = {};
  Object.keys(nestedObject).forEach(key => {
    const value = nestedObject[key];

    if (excludedFields.includes(key)) {
      return;
    }

    if (
      value !== null &&
      typeof value === 'object' &&
      !flatValuesEvenIfThereAreObjectArrays &&
      !(Array.isArray(value) && typeof value[0] !== 'object')
    ) {
      const flattened = flattenJson(value, excludedFields);
      Object.keys(flattened).forEach(subKey => {
        result[`${key}.${subKey}`] = flattened[subKey];
      });
    } else {
      result[key] = value;
    }
  });
  return result;
}

export function makeCounterText(originalText: string): string {
  const counterNumber = parseInt(originalText, 10);

  if (counterNumber < 1000) {
    return originalText;
  }

  let counterText = Math.floor(counterNumber / 1000).toString();
  const fractionalPart = Math.floor((counterNumber % 1000) / 100);
  if (fractionalPart > 0) {
    counterText += '.' + fractionalPart.toString();
  }

  counterText += 'K';

  return counterText;
}

export function formatLargeNumber(value: number | string): string {
  const number = typeof value === 'string' ? parseInt(value, 10) : value;
  const locale = 'en-US';
  const options = {
    notation: 'compact',
    compactDisplay: 'short',
    roundingMode: 'floor',
  } as any;
  return Intl.NumberFormat(locale, options).format(number);
}
